import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { DashboardResale, OnSaleOffer, OnSaleResale, ResaleDevInfo, UserOnSale } from '@interfaces/app';
import { HEADER_DONT_SAVE, HEADER_DONT_SAVE_REQ, HEADER_RESET } from '@app/constants';
import { GpsV2Service, UserService, Utils } from '@app/services';

import { environment } from 'src/environments/environment';

enum PriceComparison {
   ABOVE = 'above',
   BELOW = 'below',
   EQUAL = 'equal',
}

@Injectable({ providedIn: 'root' })
export class ResaleService {
   private cache: { [prop: string]: {} } = {};

   optionSelected: string;

   textSelected: string;

   private selected: { dev: ResaleDevInfo, value: string; amount: number; name?: string; }

   constructor(
      private readonly http: HttpClient,
      private readonly gpsServ: GpsV2Service,
      private readonly userServ: UserService,
      private readonly toastrServ: ToastrService,
      private readonly utilsServ: Utils,
   ) { }

   /** Obtener los 10 más baratas participaciones de mercado secundario */
   getResaleActiveDashboard() {
      return this.http.get<DashboardResale[]>(`${environment.apiUrlBase}/resale/dash-on-sale`, this.cache.onDash || HEADER_RESET)
         .pipe(tap(() => this.cache.onDash = {}));
   }

   /** Todas las ofertas mínimas de cada proyecto */
   getResaleActive() {
      return this.http.get<OnSaleResale[]>(`${environment.apiUrlBase}/resale/on-sale`, HEADER_DONT_SAVE);
   }

   /** Todas las ofertas por desarrollo */
   getResaleActiveOffersByDev(pid: string) {
      return this.http.get<OnSaleOffer[]>(`${environment.apiUrlBase}/resale/on-sale/${pid}`, HEADER_DONT_SAVE).pipe(
         map(r => r.sort((a, b) => Number(a.value) > Number(b.value) ? 1 : Number(b.value) > Number(a.value) ? -1 : 0)));
   }

   /** Información para reventa del desarrollo */
   getResaleDevInfo(pid: string) {
      return this.http.get<ResaleDevInfo>(`${environment.apiUrlBase}/resale/info/${pid}`);
   }

   /** Objeto con la selección de compra en mercado secuandaria con pid de desarrollo y valor de participaciones */
   setSelectedSaleToPurchase(dev: ResaleDevInfo, value: string, amount: number) {
      this.selected = { dev, value, amount };
   }

   /** Obtener objeto con la selección de compra en mercado secuandaria con pid de desarrollo y valor de participaciones */
   getSelectedSaleToPurchase() {
      return this.selected;
   }

   /** Limpiar objeto con la selección de compra en mercado secuandaria con pid de desarrollo y valor de participaciones */
   cleanSelectedSaleToPurchase() {
      this.selected = undefined;
   }

   /** Obtener información de usuario y desarrollo para reventa */
   getUserInfoForResale(pid: string) {
      return this.http.get<{
         name: string,
         limit: number;
         value: number;
         image: string;
         resaleOn: number;
         onSale: number;
      }>(`${environment.apiUrlBase}/resale/info-sale/${pid}`,
         { headers: HEADER_DONT_SAVE_REQ });
   }

   getUserResales() {
      return this.http.get<UserOnSale[]>(`${environment.apiUrlBase}/resale/historic`, this.cache.userHist || HEADER_RESET)
         .pipe(tap(() => this.cache.userHist = {}));
   }

   setOnSaleParticipations( amount: number, value: string | number, pid: string, reasonResale: string): Observable<any> {
      return this.gpsServ.getOrRequestCoordinates().pipe(
        map(({ longitude, latitude, accuracy }) => {
          const lct = this.utilsServ.utf8ToBase64(`${longitude}|${latitude}|${accuracy}`);
          const ctd = this.utilsServ.utf8ToBase64(`${pid}|${amount}|${value}`);
          const userAgent = this.utilsServ.utf8ToBase64(window.navigator.userAgent || '');
    
          const headers = HEADER_RESET.headers.set('tk', userAgent);
          const body = { ctd, lct, reason: reasonResale };
    
          return { headers, body };
        }),
        switchMap(({ headers, body }) =>
          this.http.post<any>(`${environment.apiUrlBase}/resale/on-sale`, body, { headers }).pipe(
            map((res) => {
              this.userServ.cleanUserCache();
              this.cleanCache();
              this.optionSelected = null;
              this.textSelected = null;
              return res;
            })
          )
        ),
        catchError(err => {
          this.toastrServ.warning('No fue posible obtener la ubicación para poner en reventa.');
          return throwError(() => new Error('Error al procesar la reventa: ' + err.message));
        })
      );
   }    

   cancelOnSaleParticipations(onSaleId: string) {
      const params = { param: this.utilsServ.utf8ToBase64(onSaleId) };
      const tk = window.navigator.userAgent ? this.utilsServ.utf8ToBase64(window.navigator.userAgent) : '';
      const headers = HEADER_RESET.headers.set('tk', tk);

      return this.http.put<any>(`${environment.apiUrlBase}/resale/on-sale`, params, { headers })
         .pipe(map(r => {
            this.userServ.cleanUserCache();
            this.cleanCache();
            return r;
         }));
   }

   purchaseResaleParticipations(amount: number,value: string,pid: string,otp: string,mfa?: boolean): Observable<any> {
     const exec = (lct: string) => {
       const params = { ctd: this.utilsServ.utf8ToBase64(`${pid}|${amount}|${value}`), lct};
       const headers = HEADER_DONT_SAVE.headers.set('tk', this.utilsServ.utf8ToBase64(otp));
       const service = mfa ? '/resale/purchase/mfa' : '/resale/purchase';

       return this.http.post<any>(`${environment.apiUrlBase}${service}`,params,{ headers }).pipe(map(r => {
           this.userServ.cleanUserCache();
           this.cleanCache();
           return r;
         })
       );
     };

     return this.gpsServ.getOrRequestCoordinates().pipe(
       switchMap(coords => {
         if (!coords) {
           this.toastrServ.warning('No se pudo obtener ubicación para continuar.');
           return throwError(() => new Error('Ubicación no disponible'));
         }

         const { latitude, longitude, accuracy } = coords;
         const location = this.utilsServ.utf8ToBase64(`${longitude}|${latitude}|${accuracy}`);
         return exec(location);
       }),
       catchError(err => {
         this.toastrServ.warning('Favor de dar acceso a la geolocalización para poder operar.');
         return throwError(() => err);
       })
     );
   }

   purchaseResaleParticipationsArray(selectedOffers: OnSaleOffer[], pid: string, otp: string, mfa?: boolean): Observable<any> {
     const exec = (lct: string) => {
         const ofs = selectedOffers.map(o => `${o.amount}|${o.value}`).join('#');
         const ctd = `${pid}=${ofs}`;
         const params = {
            ctd: this.utilsServ.utf8ToBase64(ctd),
            lct
         };
         const headers = HEADER_DONT_SAVE.headers.set('tk', this.utilsServ.utf8ToBase64(otp));
         const service = mfa ? '/resale/purchase-array/mfa' : '/resale/purchase-array';

         return this.http.post<any>(`${environment.apiUrlBase}${service}`, params, { headers }).pipe(
            tap(() => {
              this.userServ.cleanUserCache();
              this.cleanCache();
            })
         );
     };

     return this.gpsServ.getOrRequestCoordinates().pipe(
      take(1),
      switchMap(coords => {
         if (!coords) {
           this.toastrServ.warning('No se pudo obtener ubicación para continuar.');
           return throwError(() => new Error('Ubicación no disponible'));
         }
         const { latitude, longitude, accuracy } = coords;
         const location = this.utilsServ.utf8ToBase64(`${longitude}|${latitude}|${accuracy}`);
         return exec(location);
      }),
      catchError(err => {
         this.toastrServ.warning(
           'Favor de dar acceso a la geolocalización para poder operar.'
         );
         return throwError(() => err);
      })
     );
   }

   getCommission(amount: number, partValue: number) {
      const num = (amount * partValue) * 0.0215;
      return Math.round(num * 100) / 100;
   }

   /** Función para calcular la comisión de la compra */
   getComissionTotal(total: string) {
      const num = parseFloat(total) * 0.0215;
      const roundedNum = Math.round(num * 100) / 100;
      const result = roundedNum.toString();
      return result;
   }

   getComissionIVATotal(total: string) {
      const iva = parseFloat(total) * 0.16;
      const roundedNum = Math.round(iva * 100) / 100;
      const result = roundedNum.toString();
      return result;
   }

   /** Función para calcular la comisión de la compra */
   getCommissionNet(total: number): string {
      const num = total * 0.0215;
      const roundedNum = Math.round(num * 100) / 100;
      const result = roundedNum.toString();
      return result;
   }

   getCommissionTax(total: number): string {
      const iva = total * 0.16;
      const roundedNum = Math.round(iva * 100) / 100;
      const result = roundedNum.toString();
      return result;
   }

   getCommissionIVA(commission: number) {
      const iva = commission * 0.16;
      return Math.round(iva * 100) / 100;
   }

   getTotalParticipations(offers: OnSaleOffer[]): number {
      return offers.reduce((acc, cur) => acc + cur.amount, 0);
   }

   /**
    * Esta función selecciona las ofertas más baratas hasta alcanzar una cantidad determinada.
    * @param {OnSaleOffer[]} offers - La lista de ofertas disponibles.
    * @param {number} quantityToSelect - La cantidad total que se desea seleccionar.
    * @returns {OnSaleOffer[]} - La lista de ofertas seleccionadas.
    * @throws {Error} - Lanza un error si no hay suficientes ofertas para alcanzar la cantidad a seleccionar.
    */
   selectOffers(offers: OnSaleOffer[], quantityToSelect: number): OnSaleOffer[] {

      // Creamos una copia de las ofertas para no modificar la original y las ordenamos por precio
      const sortedOffers = [...offers]?.sort((a, b) => Number(a?.value) - Number(b?.value));

      let selectedQuantity = 0;
      let result: OnSaleOffer[] = [];

      for (const offer of sortedOffers) {
         if (selectedQuantity >= quantityToSelect) {
            break;
         }

         // Verificamos cuánto de la oferta actual podemos seleccionar
         const amountToSelect = Math.min(offer?.amount, quantityToSelect - selectedQuantity);

         result.push({
            amount: amountToSelect,
            value: offer.value,
         });

         selectedQuantity += amountToSelect;
      }

      if (selectedQuantity < quantityToSelect) {
         throw new Error('No hay suficientes ofertas para alcanzar la cantidad a seleccionar');
      }
      return result;
   }

   getTotalPrice(offers: OnSaleOffer[]): string {
      return (offers.reduce((acc, cur) => acc + (cur.amount * Number(cur.value)), 0)).toFixed(4);
   }
   getAveragePrice(offers: OnSaleOffer[]): string {
      const total = Number(this.getTotalPrice(offers));
      const totalAmount = offers.reduce((acc, cur) => acc + cur.amount, 0);
      return (total / totalAmount).toFixed(8);
   }
   /**
    * Compara el precio estimado con el precio promedio y devuelve un código que indica la relación entre ellos.
    * @param estimatedPrice El precio estimado como cadena.
    * @param averagePrice El precio promedio como cadena.
    * @returns El código que indica la relación entre el precio estimado y el precio promedio.
    */
   getEstimatedPriceCode(estimatedPrice: string, averagePrice: string): PriceComparison {
      // Convertir las cadenas de precio a números
      const estimated = Number(estimatedPrice);
      const average = Number(averagePrice);

      // Comparar los precios y devolver el código correspondiente
      if (estimated < average) {
         return PriceComparison.ABOVE; // Precio estimado por encima del precio promedio
      } else if (estimated > average) {
         return PriceComparison.BELOW; // Precio estimado por debajo del precio promedio
      } else {
         return PriceComparison.EQUAL; // Precio estimado igual al precio promedio
      }
   }


   cleanCache() { this.cache = {} }

   /**
    * Calcula el retorno estimado del desarrollador.
    *
    * @param estimatedPrice Precio estimado de la participacion.
    * @param selectedParticipations Participaciones seleccionadas.
    * @param devPerformance Rendimiento del desarrollador en porcentaje.
    * @returns El retorno estimado del desarrollador formateado como una cadena con dos decimales.
    */
   calculateEstimatedReturns(
      estimatedPrice: number,
      selectedParticipations: number,
      devPerformance: number,
   ): string {
      // Calcula el retorno estimado del desarrollador
      const estimatedDevReturns = (estimatedPrice * devPerformance * selectedParticipations) / 100;
      // Formatea el resultado con dos decimales y lo devuelve
      return estimatedDevReturns.toFixed(2);
   }
   /**
    * Calcula el valor de plusvalia estimado del desarrollador.
    *
    * @param estimatedPrice Precio estimado de la participacion.
    * @param selectedParticipations Participaciones seleccionadas.
    * @param devAppreciation Plusvalía estimada del desarrollador en porcentaje.
    * @returns El retorno estimado del desarrollador formateado como una cadena con dos decimales.
    */
   calculateEstimatedAppreciation(
      estimatedPrice: number,
      selectedParticipations: number,
      devAppreciation: number,
   ): string {
      const estimatedDevPerformance = (estimatedPrice * devAppreciation * selectedParticipations) / 100;
      return estimatedDevPerformance.toFixed(2);
   }
   calculateEstimatedTotalPerformance(
      estimatedPerformance: number,
      estimatedAppreciation: number,
   ): string {
      const estimatedTotalPerformance = estimatedPerformance + estimatedAppreciation;
      return estimatedTotalPerformance.toFixed(2);
   }
   calculateTotalInvestWithCommissions(
      totalInvestAmount: number,
      commissionNet: number,
      commissionTax: number,
   ): string {
      const totalInvestWithCommissions = totalInvestAmount + commissionNet + commissionTax;
      return totalInvestWithCommissions.toFixed(2);
   }
   calculateFinalBalance(
      initialBalance: number,
      totalInvestWithCommissions: number,
   ) {
      const finalBalance = initialBalance - totalInvestWithCommissions;
      return finalBalance.toFixed(2);
   }
   calculateEstimatedPerformancePercentage(
      totalInvestmentAmount: number,
      totalPerformance: number,
   ): string {
      const estimatedPerformancePercentage = (totalPerformance / totalInvestmentAmount) * 100;
      return estimatedPerformancePercentage.toFixed(2);
   }
}
